import numbro from 'numbro';
import React, { PureComponent } from 'react';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { WithTranslation, withTranslation } from 'react-i18next';
import { FaCube } from 'react-icons/fa';
import { connect } from 'react-redux';
import { Chart, ChartCanvas } from 'react-stockcharts';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import {
  CrossHairCursor,
  MouseCoordinateX,
  MouseCoordinateY,
} from 'react-stockcharts/lib/coordinates';
import { fitWidth } from 'react-stockcharts/lib/helper';
import { sma } from 'react-stockcharts/lib/indicator';
import { discontinuousTimeScaleProvider } from 'react-stockcharts/lib/scale';
import { LineSeries, StackedBarSeries } from 'react-stockcharts/lib/series';
import { HoverTooltip } from 'react-stockcharts/lib/tooltip';
import { last } from 'react-stockcharts/lib/utils';
import { CSSTransition } from 'react-transition-group';

import { preferenceActions } from '../../../actions';
import LoadingImage from '../../../components/general/LoadingImage';
import { PriceGroupColorEnum } from '../../../enums';
import { uiHelpers } from '../../../helpers';
import { marginWithWideRightAxis } from '../../../helpers/chart.helper';
import { ReduxProps } from '../../../types';
import BlurPlaceholder from '../../FreeUser/BlurPlaceholder';
import FeatureRequiresVIPAlert from '../../FreeUser/FeatureRequiresVIPAlert';
import TutorialQuestionMark from '../../Help/TutorialQuestionMark';
import { ProgressBarContainer } from '../../styles';

const BORDER_STYLE = '1px solid #00000044';

const lineThroughStyle = 'line-through';

const sma5 = sma()
  .options({ windowSize: 5, sourcePath: 'gainP' })
  .merge((d, c) => {
    d.sma5 = c;
  })
  .accessor(d => d.sma5);

interface Props {
  n: number;
  nGain: number;
  nLoss: number;
  nEqual: number;
}

interface States {
  showHistograms: boolean;
}
class GainLostChart extends PureComponent<
  Props &
    WithTranslation &
    ReduxProps<typeof mapStateToProps> & {
      // fitWidth()
      width: number;
      ratio: number;
    },
  States
> {
  constructor(props) {
    super(props);

    this.state = {
      showHistograms: false,
    };
  }

  render() {
    const { t, histograms, loading, nGain, nLoss, nEqual, n } = this.props;
    const { showHistograms } = this.state;

    return (
      <div>
        <ProgressBarContainer>
          <span className="label">
            <FaCube /> {t('stock-gain-loss')}{' '}
            <TutorialQuestionMark tutorialId={17} />
          </span>
          {/* FIXME: we need to refactor this code to reduce code duplication */}
          {/* jscpd:ignore-start */}
          <ProgressBar
            style={{ cursor: 'pointer' }}
            onClick={() => this.setState({ showHistograms: !showHistograms })}
          >
            <ProgressBar
              key={0}
              variant="success"
              now={(100 * nGain) / n}
              label={`${nGain} ${t('Gain')}`}
            />
            <ProgressBar
              key={1}
              variant="secondary"
              now={(100 * nEqual) / n}
              label={`${nEqual} =`}
            />
            <ProgressBar
              key={2}
              variant="danger"
              now={(100 * nLoss) / n}
              label={`${nLoss} ${t('Loss')}`}
            />
          </ProgressBar>
          {/* jscpd:ignore-end */}
        </ProgressBarContainer>
        <CSSTransition
          in={showHistograms}
          timeout={300}
          classNames="animated-height-430"
          unmountOnExit
        >
          <div>
            <BlurPlaceholder featureKey="ratio">
              <LoadingImage displayAsBlock={true} loading={loading} />
              {histograms && this.chart()}
            </BlurPlaceholder>
            <FeatureRequiresVIPAlert featureTitle={t('feature-ratio')} />
          </div>
        </CSSTransition>
      </div>
    );
  }

  toggleIndex = (indexName: string) => {
    const { dispatch, region, usHistogramIndexType, hkHistogramIndexType } =
      this.props;
    let indexType: string[] =
      region === 'us' ? [...usHistogramIndexType] : [...hkHistogramIndexType];
    indexType.includes(indexName)
      ? (indexType = indexType.filter(v => v !== indexName))
      : (indexType = [indexName]);

    dispatch(preferenceActions.setHistogramIndexType(region, indexType));
  };

  toggleGroup360s = (groupName: string) => {
    const { dispatch, histogramGroupType } = this.props;
    let groupType: string[] = [...histogramGroupType];
    groupType.includes(groupName)
      ? (groupType = groupType.filter(v => v !== groupName))
      : (groupType = [...groupType, groupName]);

    dispatch(preferenceActions.setHistogramGroupType(groupType));
  };

  calcHistogramData = () => {
    const { histograms } = this.props;
    for (const d of histograms) {
      d.x =
        d.date == undefined
          ? new Date(d.date)
          : new Date(d.date.replace('Z', ''));
      d.gain = Number.parseInt(d.gain);
      d.loss = Number.parseInt(d.loss);
      d.sum = d.gain + d.loss;
      d.total_gp_count = [
        d.total_no_gp0,
        d.total_no_gp1,
        d.total_no_gp2,
        d.total_no_gp3,
      ]
        .filter(Boolean)
        .reduce((a, b) => a + b, 0);
      d.gainP = d.gain / d.sum;
      d.ratio_gp0 =
        d.total_no_gp0 === null ? undefined : d.total_no_gp0 / d.total_gp_count;
      d.ratio_gp1 =
        d.total_no_gp1 === null ? undefined : d.total_no_gp1 / d.total_gp_count;
      d.ratio_gp2 =
        d.total_no_gp2 === null ? undefined : d.total_no_gp2 / d.total_gp_count;
      d.ratio_gp3 =
        d.total_no_gp3 === null ? undefined : d.total_no_gp3 / d.total_gp_count;
    }
  };

  chart() {
    const {
      dispatch,
      t,
      histograms,
      width,
      ratio,
      region,
      histogramYellowSMA5,
      hkHistogramIndexType,
      usHistogramIndexType,
      histogramGroupType,
    } = this.props;

    const groupRP360sArray = [
      {
        groupName: '0',
        color: PriceGroupColorEnum.group0,
        translation: 'lowest quarter',
      },
      {
        groupName: '1',
        color: PriceGroupColorEnum.group1,
        translation: 'lower quarter',
      },
      {
        groupName: '2',
        color: PriceGroupColorEnum.group2,
        translation: 'upper quarter',
      },
      {
        groupName: '3',
        color: PriceGroupColorEnum.group3,
        translation: 'highest quarter',
      },
    ];

    const usIndexesArray = [
      {
        indexName: 'INX',
        indexColor: 'rgba(0,0,255, 0.5)',
      },
      {
        indexName: 'DJI',
        indexColor: '#00000088',
      },
      {
        indexName: 'IXIC',
        indexColor: 'rgba(255,0,0, 1)',
      },
    ];

    const tooltipContent = ({ currentItem }) => {
      const {
        gain,
        loss,
        sum,
        HSI_close,
        DJI_close,
        IXIC_close,
        GSPC_close,
        date,
      } = currentItem;
      return {
        x: uiHelpers.formatDate(date),
        y: [
          {
            label: t('Gain'),
            value: `${gain} (${numbro(gain / sum).format('0.0%')})`,
          },
          {
            label: t('Loss'),
            value: `${loss} (${numbro(loss / sum).format('0.0%')})`,
          },
          {
            label: t('hsi'),
            value: HSI_close,
          },
          {
            label: t('dji'),
            value: DJI_close,
          },
          {
            label: t('ixic'),
            value: IXIC_close,
          },
          {
            label: t('inx'),
            value: GSPC_close,
          },
        ].filter(line => line.value),
      };
    };

    this.calcHistogramData();

    const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor(
      d => d.x
    );
    const { data, xScale, xAccessor, displayXAccessor } = xScaleProvider(
      sma5(histograms.filter(d => d.sum > 200))
    );

    const xExtents = [xAccessor(last(data)), xAccessor(last(data)) - 120];

    const yellowLegend = (
      <span
        style={{ cursor: 'pointer' }}
        onClick={() => {
          dispatch(
            preferenceActions.setHistogramYellowSMA5(!histogramYellowSMA5)
          );
        }}
      >
        <span
          style={{
            backgroundColor: '#ffff0099',
            border: BORDER_STYLE,
          }}
        >
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
        <span
          style={{
            marginRight: '8px',
          }}
        >
          <small
            style={{ textDecoration: !histogramYellowSMA5 && lineThroughStyle }}
          >
            &nbsp;{t('yellow-ma')}
          </small>
        </span>
      </span>
    );

    return (
      <div>
        <ChartCanvas
          ratio={ratio}
          width={width}
          height={400}
          type="hybrid"
          margin={marginWithWideRightAxis}
          seriesName="Gain Loss"
          xExtents={xExtents}
          data={data}
          xAccessor={xAccessor}
          displayXAccessor={displayXAccessor}
          xScale={xScale}
          clamp="both"
        >
          <Chart id={1} yExtents={[0, 1]}>
            <XAxis axisAt="bottom" orient="bottom" />
            <YAxis axisAt="left" orient="left" />
            <MouseCoordinateX
              at="bottom"
              orient="bottom"
              displayFormat={uiHelpers.formatDateLocal}
            />
            <MouseCoordinateY
              at="left"
              orient="left"
              displayFormat={d => numbro(d).format('0.00')}
            />
            <StackedBarSeries
              yAccessor={[d => d.gain / d.sum, d => d.loss / d.sum]}
              fill={(_, i) => ['#28a745', '#dc3545'][i]}
              widthRatio={0.9}
              stroke={true}
              clip={false}
            />
            {histogramYellowSMA5 && (
              <LineSeries
                yAccessor={sma5.accessor()}
                stroke="#ffff0099"
                strokeWidth={4}
              />
            )}
            <HoverTooltip tooltipContent={tooltipContent} fontSize={15} />
          </Chart>
          {region == 'us' && usHistogramIndexType.includes('INX') && (
            <Chart id={2} yExtents={d => d.GSPC_close}>
              {usHistogramIndexType.length === 1 && (
                <YAxis axisAt="right" orient="right" />
              )}
              <LineSeries
                yAccessor={d => {
                  return d.GSPC_close;
                }}
                stroke="rgba(0,0,255, 0.5)"
                strokeWidth={2}
              />
            </Chart>
          )}
          {region == 'us' && usHistogramIndexType.includes('DJI') && (
            <Chart id={3} yExtents={d => d.DJI_close}>
              {usHistogramIndexType.length === 1 && (
                <YAxis axisAt="right" orient="right" />
              )}
              <LineSeries
                yAccessor={d => {
                  return d.DJI_close;
                }}
                stroke="#00000088"
                strokeWidth={2}
              />
            </Chart>
          )}
          {region == 'us' && usHistogramIndexType.includes('IXIC') && (
            <Chart id={4} yExtents={d => d.IXIC_close}>
              {usHistogramIndexType.length === 1 && (
                <YAxis axisAt="right" orient="right" />
              )}
              <LineSeries
                yAccessor={d => {
                  return d.IXIC_close;
                }}
                stroke="rgba(255,0,0, 1)"
                strokeWidth={2}
              />
            </Chart>
          )}
          {region == 'hk' && hkHistogramIndexType.includes('HSI') && (
            <Chart id={4} yExtents={d => d.HSI_close}>
              <YAxis axisAt="right" orient="right" />
              <LineSeries
                yAccessor={d => {
                  return d.HSI_close;
                }}
                stroke="rgba(0,0,255, 0.5)"
                strokeWidth={2}
              />
            </Chart>
          )}
          {histogramGroupType.includes('0') && (
            <Chart id={5} yExtents={[0, 1]}>
              <LineSeries
                yAccessor={d => {
                  return d.ratio_gp0;
                }}
                stroke={PriceGroupColorEnum.group0}
                strokeWidth={2}
              />
            </Chart>
          )}
          {histogramGroupType.includes('1') && (
            <Chart id={5} yExtents={[0, 1]}>
              <LineSeries
                yAccessor={d => {
                  return d.ratio_gp1;
                }}
                stroke={PriceGroupColorEnum.group1}
                strokeWidth={2}
              />
            </Chart>
          )}
          {histogramGroupType.includes('2') && (
            <Chart id={5} yExtents={[0, 1]}>
              <LineSeries
                yAccessor={d => {
                  return d.ratio_gp2;
                }}
                stroke={PriceGroupColorEnum.group2}
                strokeWidth={2}
              />
            </Chart>
          )}
          {histogramGroupType.includes('3') && (
            <Chart id={5} yExtents={[0, 1]}>
              <LineSeries
                yAccessor={d => {
                  return d.ratio_gp3;
                }}
                stroke={PriceGroupColorEnum.group3}
                strokeWidth={2}
              />
            </Chart>
          )}
          <CrossHairCursor />
        </ChartCanvas>
        <div className="text-center">
          {yellowLegend}
          {region == 'us' &&
            usIndexesArray.map(v => (
              <>
                <span
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.toggleIndex(v.indexName)}
                >
                  <span
                    style={{
                      backgroundColor: v.indexColor,
                      border: BORDER_STYLE,
                    }}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  <span
                    style={{
                      marginRight: '8px',
                    }}
                  >
                    <small
                      style={{
                        textDecoration:
                          !usHistogramIndexType.includes(v.indexName) &&
                          lineThroughStyle,
                      }}
                    >
                      &nbsp;{t(v.indexName.toLowerCase())}
                    </small>
                  </span>
                </span>
              </>
            ))}
          {region == 'hk' && (
            <>
              <span
                style={{ cursor: 'pointer' }}
                onClick={() => this.toggleIndex('HSI')}
              >
                <span
                  style={{
                    backgroundColor: 'rgba(0,0,255, 0.5)',
                    border: BORDER_STYLE,
                  }}
                >
                  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                </span>
                <span
                  style={{
                    marginRight: '8px',
                  }}
                >
                  <small
                    style={{
                      textDecoration:
                        !hkHistogramIndexType.includes('HSI') &&
                        lineThroughStyle,
                    }}
                  >
                    &nbsp;{t('hsi')}
                  </small>
                </span>
              </span>
            </>
          )}
          {groupRP360sArray.map((v, i) => (
            <span
              key={i}
              style={{ cursor: 'pointer' }}
              onClick={() => this.toggleGroup360s(v.groupName)}
            >
              <span
                style={{
                  backgroundColor: v.color,
                  border: BORDER_STYLE,
                }}
              >
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
              <span
                style={{
                  marginRight: '8px',
                }}
              >
                <small
                  style={
                    histogramGroupType.includes(v.groupName)
                      ? {}
                      : {
                          textDecoration: lineThroughStyle,
                        }
                  }
                >
                  &nbsp;{t(v.translation)}
                </small>
              </span>
            </span>
          ))}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { histograms, loading } = state.dailyprice.histograms;
  const {
    region,
    histogramYellowSMA5,
    hkHistogramIndexType,
    usHistogramIndexType,
    histogramGroupType,
  } = state.preference;
  return {
    histograms,
    loading,
    region,
    histogramYellowSMA5,
    hkHistogramIndexType,
    usHistogramIndexType,
    histogramGroupType,
  };
}

export default withTranslation()(
  connect(mapStateToProps)(fitWidth(GainLostChart))
) as React.ComponentType<Props>; // fitWidth will return the wrong type;
